import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../../common/types';
import { SMLButton } from '../../../../components/primitives/SMLButton';
import { useUpgradePrompt } from '../../../../hooks/useUpgradePrompt';
import { SMLUpgradeFeature } from '../../../../config/music24PlanCapabilities';
import { useRollout } from './RolloutContext';
import type { FitResult } from './rolloutModel';
import { rolloutMessages } from './rolloutMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the run sheet; each part uses its own slice of it */
import styles from './Rollout.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** "May" of `YYYY-MM`. */
function monthName(intl: ReturnType<typeof useIntl>, month: string): string {
  return intl.formatDate(new Date(`${month}-01T00:00:00Z`), { month: 'long', timeZone: 'UTC' });
}

const width = (part: number, limit: number): React.CSSProperties => ({
  width: `${limit === 0 ? 0 : Math.min(100, (part / limit) * 100)}%`,
});

/**
 * Where the Free limits show (design rollout-plan e1, owner decision 8): one bar per month that the
 * drafts fall in (used, and what this plan needs), and the upgrade line. A member sees the counts
 * with no "See plans" (`isOwner`).
 */
const FreePlanLimitsComponent: React.FC<{ fit: FitResult; isOwner: boolean; showUpsell: boolean }> = ({
  fit,
  isOwner,
  showUpsell,
}) => {
  const intl = useIntl();
  const openUpgrade = useUpgradePrompt();
  const { item } = useRollout();
  if (fit.months.length === 0 && !showUpsell) {
    return null;
  }
  return (
    <>
      {fit.months.length > 0 && (
        <div className={styles.qline}>
          {fit.months.map((entry) => {
            const month = monthName(intl, entry.month);
            return (
              <div key={entry.month} className={styles.q}>
                <b>{intl.formatMessage(rolloutMessages.monthUse, { month, used: entry.used, limit: entry.limit })}</b>
                <div className={styles.qbar}>
                  <i className={styles.qbarPart} style={width(entry.used, entry.limit)} />
                  <i className={clsx(styles.qbarPart, styles.qbarNew)} style={width(entry.needs, entry.limit)} />
                </div>
                <small>
                  {intl.formatMessage(rolloutMessages.monthNeeds, {
                    needs: entry.needs,
                    month,
                    fits: entry.fits,
                    all: entry.fits === entry.needs ? 'yes' : 'no',
                  })}
                </small>
              </div>
            );
          })}
        </div>
      )}
      {showUpsell && isOwner && (
        <div className={styles.upl}>
          <div className={styles.grow}>
            <b>{intl.formatMessage(rolloutMessages.upsellTitle)}</b>
            <small>{intl.formatMessage(rolloutMessages.upsellBody)}</small>
          </div>
          <SMLButton variant="primary" onClick={() => openUpgrade(SMLUpgradeFeature.scheduledPosts, item.title)}>
            {intl.formatMessage(rolloutMessages.seePlans)}
          </SMLButton>
        </div>
      )}
    </>
  );
};

export const FreePlanLimits = TypedMemo(FreePlanLimitsComponent);
